import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { 
  Button, 
  OTPInput, 
  Logo, 
  DualPaneLayout,
  colors, 
  spacing, 
  typography 
} from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { handleApiError, logError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface OTPVerificationScreenProps {
  verificationType?: 'email' | 'phone';
  targetValue?: string; // email or phone number
  onVerificationComplete?: () => void;
  onBack?: () => void;
}

export const OTPVerificationScreen: React.FC<OTPVerificationScreenProps> = ({
  verificationType: verificationTypeProp,
  targetValue: targetValueProp,
  onVerificationComplete,
  onBack
}) => {
  const styles = useThemedStyles(getStyles);
  const {
    handleBackToLogin: defaultOnBack,
    handleLogout,
    showNotification,
    navigateTo,
    pendingEmail,
    isNewUser,
    routeParams
  } = useAppContext();

  const { user, setUser, setTokens, setAuthState, setPhase1Token, setUserFromPhase1Token } = useAppStore();
  const { t } = useLocale();
  const typo = useTypography();

  const [isLoading, setIsLoading] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  // Matches the server's 60s per-number cooldown, so "Resend" isn't offered
  // while it would just be refused.
  const [resendIn, setResendIn] = useState(60);
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // AppNavigator renders this screen with no props — unified login/phone/email-login flows
  // pass their data via routeParams instead (see navigateTo('otp-verification', undefined,
  // {verificationType, mobile|email}) in UnifiedLoginScreen.tsx).
  const routeVerificationType = (routeParams as any)?.verificationType as
    | 'email' | 'phone' | 'phone-whatsapp' | 'email-login' | 'invitation-email' | undefined;
  const verificationType = verificationTypeProp || routeVerificationType || 'email';
  const targetValue = targetValueProp || (routeParams as any)?.mobile || (routeParams as any)?.email;

  const isUnifiedPhoneLogin = verificationType === 'phone-whatsapp';
  const isUnifiedEmailLogin = verificationType === 'email-login';
  const isInvitationEmail = verificationType === 'invitation-email';
  const isLegacyPhoneVerification = verificationType === 'phone';

  const routeToMembershipsOrWizard = async () => {
    try {
      // A pending invitation must also route to business-picker, not business-creation-wizard,
      // even with zero active memberships — see the identical fix in UnifiedLoginScreen.tsx.
      const [membershipsResponse, invitationsResponse] = await Promise.all([
        apiService.getMemberships(),
        apiService.getPendingInvitations(),
      ]);
      const memberships = membershipsResponse.data || [];
      const invitations = invitationsResponse.data || [];
      navigateTo((memberships.length === 0 && invitations.length === 0) ? 'business-creation-wizard' : 'business-picker');
    } catch (err) {
      logError(err, 'OTPVerificationScreen');
      navigateTo('business-picker');
    }
  };

  const handleVerification = async () => {
    // Every code the server issues is 6 digits (OTP_LENGTH default).
    const expectedLength = 6;
    if (otpCode.length !== expectedLength) {
      showNotification('warning', 'Invalid Code', `Please enter the complete ${expectedLength}-digit verification code.`);
      return;
    }

    setIsLoading(true);

    try {
      logError('Starting verification with code:', 'OTPVerificationScreen');

      if (isUnifiedPhoneLogin) {
        const response = await apiService.verifyPhoneOTP(targetValue, otpCode, trustDevice);
        if (response.statusCode === 1000 && response.data?.token) {
          setPhase1Token(response.data.token);
          setUserFromPhase1Token(response.data.token);
          apiService.setAuthToken(response.data.token);
          if (response.data.refreshToken) apiService.setRefreshToken(response.data.refreshToken);
          await routeToMembershipsOrWizard();
        } else {
          const { title, message, type } = handleApiError(response);
          showNotification(type, title, message);
        }
        return;
      }

      if (isUnifiedEmailLogin) {
        const response = await apiService.verifyEmailLoginOTP(targetValue, otpCode, trustDevice);
        if (response.statusCode === 1000 && response.data?.token) {
          setPhase1Token(response.data.token);
          setUserFromPhase1Token(response.data.token);
          apiService.setAuthToken(response.data.token);
          if (response.data.refreshToken) apiService.setRefreshToken(response.data.refreshToken);
          await routeToMembershipsOrWizard();
        } else {
          const { title, message, type } = handleApiError(response);
          showNotification(type, title, message);
        }
        return;
      }

      if (isInvitationEmail) {
        // Same OTP verification as unified email login — findOrCreateByVerifiedContact
        // resolves the EXISTING invited user (created password-less by inviteUser) rather
        // than creating a duplicate. Once we hold a verified phase-1 token for them, activate
        // the pending BusinessUser row and land directly on that business, skipping the
        // generic memberships picker since we already know exactly which business this
        // invitation was for.
        const response = await apiService.verifyEmailLoginOTP(targetValue, otpCode);
        if (response.statusCode === 1000 && response.data?.token) {
          setPhase1Token(response.data.token);
          setUserFromPhase1Token(response.data.token);
          apiService.setAuthToken(response.data.token);

          const invitationToken = (global as any).invitationToken;
          const invitationBusinessId = (global as any).invitationBusinessId;
          (global as any).invitationToken = undefined;
          (global as any).invitationBusinessId = undefined;

          if (!invitationToken) {
            showNotification('error', 'Invitation Error', 'Invitation details were lost — please open the invitation link again.');
            navigateTo('unified-login');
            return;
          }

          const activateResponse = await apiService.activateInvitation(invitationToken);
          if (activateResponse.statusCode !== 1000) {
            const { title, message, type } = handleApiError(activateResponse);
            showNotification(type, title, message);
            return;
          }

          if (invitationBusinessId) {
            const selectResponse = await apiService.selectBusiness(invitationBusinessId);
            if (selectResponse.statusCode === 1000 && selectResponse.data) {
              const { token: businessToken, refreshToken } = selectResponse.data;
              await setAuthState({
                tokens: { token: businessToken, refreshToken },
                user: user || {
                  id: '', email: '', firstName: '', lastName: '', username: '',
                  isEmailVerified: true, isActive: true,
                  createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
                },
                source: 'login'
              });
              apiService.setAuthToken(businessToken);
              apiService.setRefreshToken(refreshToken);
              setPhase1Token(null);
              showNotification('success', 'Welcome!', 'You have joined the business.');
              navigateTo('home');
              return;
            }
          }

          // Fall back to the picker if we couldn't auto-select the invited business for any
          // reason (e.g. selectBusiness failed) — the membership is active either way.
          await routeToMembershipsOrWizard();
        } else {
          const { title, message, type } = handleApiError(response);
          showNotification(type, title, message);
        }
        return;
      }

      if (isLegacyPhoneVerification) {
        // Legacy phone verification (e.g. WhatsApp channel setup) is handled in its own screen.
        showNotification('info', 'Phone Verification', 'Phone verification is handled in the WhatsApp setup screen.');
        if (onVerificationComplete) {
          onVerificationComplete();
        }
        return;
      }

      // Email verification (legacy register→verify flow)
      const email = targetValue || pendingEmail;
      const response = await apiService.verifyEmail(email, otpCode);

      logError('Verification response:', 'OTPVerificationScreen');

      if (response.statusCode === 1000) {
        if (isNewUser) {
          showNotification('success', 'Email Verified', 'Please sign in to continue.');
          navigateTo('unified-login');
        } else {
          if (response.data && response.user) {
            const { token, refreshToken } = response.data;
            const userData = response.user;
            
            if (token && refreshToken && userData) {
              // 🔥 NEW: Use atomic setAuthState() for email reactivation
              await setAuthState({
                tokens: { token, refreshToken },
                user: {
                  id: userData.id || userData._id,
                  email: userData.email,
                  firstName: userData.name ? userData.name.split(' ')[0] || '' : '',
                  lastName: userData.name ? userData.name.split(' ').slice(1).join(' ') || '' : '',
                  username: userData.email ? userData.email.split('@')[0] : '',
                  isEmailVerified: userData.emailVerified || false,
                  isActive: true,
                  mobile: userData.mobile || userData.phone,
                  phone: userData.phone || userData.mobile,
                  timezone: userData.timezone,
                  timezoneOffset: userData.timezoneOffset,
                  country: userData.country,
                  city: userData.city,
                  preferences: userData.preferences,
                  createdAt: userData.createdAt || new Date().toISOString(),
                  updatedAt: userData.updatedAt || new Date().toISOString()
                },
                source: 'login'
              });
              
              console.log('✅ [OTPVerification] Auth state set atomically after email verification');
              showNotification('success', 'Login Successful', 'Email verified! Welcome back!');
              navigateTo('home');
            } else {
              showNotification('error', 'Verification Error', 'Invalid response data from server.');
            }
          } else {
            showNotification('error', 'Verification Error', 'No user data received from server.');
          }
        }
      } else {
        // Handle API error with unified error handling
        const { title, message, type } = handleApiError(response);
        showNotification(type, title, message);
      }
    } catch (error: any) {
      logError(error, 'OTPVerificationScreen');
      
      // Handle error with unified error handling
      const { title, message, type } = handleApiError(error);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendIn > 0) return;
    setIsLoading(true);

    try {
      logError('Resending verification code to:', 'OTPVerificationScreen');

      if (isUnifiedPhoneLogin) {
        await apiService.sendPhoneOTP(targetValue);
        showNotification('success', 'Code Sent', 'A new code has been sent via WhatsApp.');
      } else if (isUnifiedEmailLogin) {
        await apiService.sendEmailLoginOTP(targetValue);
        showNotification('success', 'Code Sent', 'A new code has been sent to your email.');
      } else if (isLegacyPhoneVerification) {
        showNotification('info', 'Resend Code', 'Please use the resend option in the WhatsApp setup screen.');
      } else {
        const email = targetValue || pendingEmail;
        await apiService.sendVerificationEmail(email);
        showNotification('success', 'Code Sent', 'Verification code has been resent to your email.');
      }
    } catch (error: any) {
      logError(error, 'OTPVerificationScreen');
      
      // Handle error with unified error handling
      const { title, message, type } = handleApiError(error);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
      setResendIn(60);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (isUnifiedPhoneLogin || isUnifiedEmailLogin) {
      navigateTo('unified-login');
    } else if (defaultOnBack) {
      defaultOnBack();
    }
  };

  // Get the target value (email or phone) for display
  const displayValue = targetValue || (verificationType === 'email' ? pendingEmail : '');
  const isPhoneVerification = isLegacyPhoneVerification || isUnifiedPhoneLogin;
  const otpLength = 6;

  return (
    <DualPaneLayout
      title="DISLIX"
      subtitle={isPhoneVerification ? t('auth.otpTitlePhone') : t('auth.otpTitle')}
      showMobileLogo={false}
    >
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoidingView}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.logoContainer}>
              <Logo size="medium" />
            </View>
          </View>

          {/* Main Content */}
          <ScrollView contentContainerStyle={styles.content}>
            <Text style={[styles.title, { fontFamily: typo.fontFamily.bold }]}>
              {isPhoneVerification ? t('auth.otpTitlePhone') : t('auth.otpTitle')}
            </Text>
            <Text style={[styles.subtitle, { fontFamily: typo.fontFamily.primary }]}>
              {t('auth.otpSubtitle')}
            </Text>
            <Text style={styles.email}>{displayValue}</Text>

            {/* OTP Input */}
            <OTPInput
              value={otpCode}
              onChangeText={setOtpCode}
              length={otpLength}
              error={''} // Removed local error state
              disabled={isLoading}
              autoFocus={true}
            />

            {/* Trust this device — widens the sliding refresh window from a short (1 day)
                inactivity leash to a long one (up to 30 days), so the session survives being
                closed and reopened without forcing a fresh OTP every time. */}
            {(isUnifiedPhoneLogin || isUnifiedEmailLogin) && (
              <TouchableOpacity
                onPress={() => setTrustDevice(prev => !prev)}
                disabled={isLoading}
                style={styles.trustDeviceRow}
                activeOpacity={0.7}
              >
                <Ionicons
                  name={trustDevice ? 'checkbox' : 'square-outline'}
                  size={20}
                  color={trustDevice ? colors.primary : colors.textLight}
                />
                <Text style={[styles.trustDeviceText, { fontFamily: typo.fontFamily.primary }]}>
                  {t('auth.trustThisDevice') || 'Remember this device for 30 days'}
                </Text>
              </TouchableOpacity>
            )}

            {/* Verify Button */}
            <Button
              title={isPhoneVerification ? t('auth.otpTitlePhone') : t('auth.otpTitle')}
              onPress={handleVerification}
              variant="primary"
              size="lg"
              fullWidth
              loading={isLoading}
              style={styles.verifyButton}
            />

            {/* Resend Code Button */}
            <TouchableOpacity 
              onPress={handleResendCode}
              disabled={isLoading || resendIn > 0}
              style={styles.resendButton}
            >
              <Text style={[
                styles.resendText,
                { fontFamily: typo.fontFamily.medium },
                (isLoading || resendIn > 0) && styles.disabledText
              ]}>
                {resendIn > 0 ? t('auth.resendCodeIn', { seconds: resendIn }) : t('auth.resendCode')}
              </Text>
            </TouchableOpacity>

            {/* Back Button */}
            <TouchableOpacity
              onPress={handleBack}
              disabled={isLoading}
              style={styles.backButton}
            >
              <Text style={[
                styles.backText,
                { fontFamily: typo.fontFamily.medium },
                isLoading && styles.disabledText
              ]}>
                {isNewUser ? t('auth.backToRegistration') : t('auth.backToLogin')}
              </Text>
            </TouchableOpacity>

            {/* Logout — lets the user abandon this in-progress verification (e.g. wrong
                account/number) and clear any state without waiting to reach a business/plan
                step first. */}
            <TouchableOpacity
              onPress={handleLogout}
              disabled={isLoading}
              style={styles.backButton}
            >
              <Text style={[
                styles.backText,
                { fontFamily: typo.fontFamily.medium },
                isLoading && styles.disabledText
              ]}>
                {t('auth.logout') || 'Log out'}
              </Text>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  logoContainer: {
    alignItems: 'center',
  },
  content: {
    flex: 1,
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.xl,
    alignItems: 'center',
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '700', // bold
    color: colors.textDark,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  email: {
    fontSize: typography.fontSize.base,
    color: colors.primary,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  trustDeviceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.lg,
    alignSelf: 'flex-start',
  },
  trustDeviceText: {
    marginLeft: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  resendButton: {
    marginTop: spacing.md,
    marginBottom: spacing.md,
  },
  resendText: {
    color: colors.primary,
    fontSize: typography.fontSize.sm,
  },
  disabledText: {
    color: colors.textLight,
  },
  backButton: {
    marginTop: spacing.md,
  },
  backText: {
    color: colors.textLight,
    fontSize: typography.fontSize.sm,
  },
  verifyButton: {
    marginTop: spacing.lg,
    marginBottom: spacing.md,
    width: '100%',
  },
});
